import React, { useEffect, useMemo, useRef, useState } from "react";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import "./EventEvidenceComparison.css";
import type {
  ChangeRowV1,
  ComparisonApiErrorV1,
  EventEvidenceComparisonPacketV1,
  FamilyCoverageV1,
  SourceRefV1,
  VersionListItemV1,
  VersionPageV1,
} from "@shared/eventEvidenceComparison";

type Props = { dossierId: string; asOf: string | null; boundaryValid?: boolean };
type ReadState = "loading" | "ready" | "empty" | "error";
type SectionRows = { title: string; rows: readonly ChangeRowV1[] };
const ROWS_PER_PAGE = 20;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validVersionPage(value: unknown): value is VersionPageV1 {
  if (!isRecord(value) || value.mode !== "event-version-page" || !isRecord(value.dossier) || !Array.isArray(value.versions)) return false;
  return value.versions.every((item) => isRecord(item) && typeof item.reportId === "string" && Number.isSafeInteger(item.sequence)
    && typeof item.createdAt === "string" && (item.acknowledgedAt === null || typeof item.acknowledgedAt === "string")
    && typeof item.receiptState === "string" && typeof item.manifestDigest === "string"
    && (item.receiptDigest === null || typeof item.receiptDigest === "string"))
    && (value.nextBeforeSequence === null || Number.isSafeInteger(value.nextBeforeSequence));
}

function validPacket(value: unknown): value is EventEvidenceComparisonPacketV1 {
  if (!isRecord(value) || value.version !== "tsm-event-evidence-comparison-v1" || !isRecord(value.left)
    || !isRecord(value.right) || !isRecord(value.left.metadata) || !isRecord(value.right.metadata)
    || !isRecord(value.metadata) || typeof value.metadata.dossierId !== "string"
    || !isRecord(value.result) || !isRecord(value.result.presentation) || !isRecord(value.result.confidenceComparison)
    || !Array.isArray(value.result.changes)
    || !Array.isArray(value.result.uncertainty) || !Array.isArray(value.result.gaps)
    || !Array.isArray(value.result.familyCoverage) || !Array.isArray(value.result.conclusions)
    || !isRecord(value.boundary) || typeof value.digest !== "string") return false;
  return true;
}

function packetMatchesRequest(
  packet: EventEvidenceComparisonPacketV1,
  request: { dossierId: string; fromReportId: string; fromSequence: number; toReportId: string; toSequence: number; asOf: string | null },
) {
  const expectedRule = request.asOf === null ? "no-boundary" : "acknowledged-at-or-before";
  return packet.metadata.dossierId === request.dossierId
    && packet.left.metadata.reportId === request.fromReportId
    && packet.left.metadata.sequence === request.fromSequence
    && packet.right.metadata.reportId === request.toReportId
    && packet.right.metadata.sequence === request.toSequence
    && packet.boundary.asOf === request.asOf
    && packet.boundary.rule === expectedRule;
}

async function readJson(response: Response): Promise<unknown> {
  try { return await response.json(); } catch { return null; }
}

function errorMessage(value: unknown, fallback: string) {
  if (!isRecord(value)) return fallback;
  const message = typeof value.message === "string" ? value.message : typeof value.error === "string" ? value.error : null;
  return message ? message.replaceAll("_", " ") : fallback;
}

function display(value: unknown) {
  if (value === null || value === undefined) return "Not retained";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function dateLabel(value: string | null | undefined) {
  return value ? `${value} (UTC)` : "Not retained";
}

function receiptLabel(version: VersionListItemV1) {
  if (version.receiptState === "missing") return "Receipt missing · cannot compare";
  if (version.receiptState === "invalid") return "Receipt invalid · cannot compare";
  if (!version.acknowledgedAt) return "Acknowledgment unavailable · cannot compare";
  return `Receipt verified · acknowledged ${dateLabel(version.acknowledgedAt)}`;
}

function sourceRefLabel(ref: SourceRefV1, index: number) {
  return <li key={`${ref.side}:${ref.recordKey}:${ref.rawDigest ?? ""}:${ref.acknowledgedAt}:${index}`}>
    <p><strong>{ref.side === "left" ? "Earlier" : "Later"} saved source</strong> · {ref.family} · record <code>{ref.recordKey}</code></p>
    <p>Report <code>{ref.outerReportId}</code> · manifest digest <code>{ref.outerManifestDigest}</code>{ref.nestedReportId ? <> · nested report <code>{ref.nestedReportId}</code> · nested manifest <code>{ref.nestedManifestDigest ?? "not retained"}</code></> : null}</p>
    <p>Provider {ref.sourceProvider ?? "not retained"} · source report {ref.sourceReportKey ?? "not retained"} · identifier {ref.sourceIdentifier ?? "not retained"}</p>
    <p>Retained URL: <code>{ref.sourceUrl ?? "not retained"}</code> · raw digest {ref.rawDigest ? <code>{ref.rawDigest}</code> : "not retained"} · object digest {ref.objectDigest ? <code>{ref.objectDigest}</code> : "not retained"}</p>
    <p>Source clock {dateLabel(ref.sourceClock)} · observation clock {dateLabel(ref.observationClock)} · request started {dateLabel(ref.requestStartedAt)} · response received {dateLabel(ref.responseReceivedAt)} · retention started {dateLabel(ref.retentionStartedAt)} · acknowledgment {dateLabel(ref.acknowledgedAt)}.</p>
  </li>;
}

function RowGroup({ family, kind, rows }: { family: string; kind: string; rows: readonly ChangeRowV1[] }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const visible = rows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
  return <details className="event-evidence-comparison-group">
    <summary>{family} · {kind.replaceAll("-", " ")} ({rows.length})</summary>
    <ol start={page * ROWS_PER_PAGE + 1}>
      {visible.map((row) => <li key={row.entryDigest}>
        <article aria-label={`${row.family} ${row.kind} ${row.fieldPath ?? row.identityKey}`}>
          <h5>{row.fieldPath ?? row.identityKey}</h5>
          <p><strong>Classification:</strong> {row.kind.replaceAll("-", " ")}</p>
          <div className="event-evidence-comparison-values">
            <section aria-label="Earlier value"><h6>Earlier</h6><p>{row.previous ? display(row.previous.value) : "No earlier value"}</p>{row.previous && <p>State: {row.previous.state.replaceAll("-", " ")}{row.previous.reasons.length ? ` · ${row.previous.reasons.join("; ")}` : ""}</p>}</section>
            <section aria-label="Later value"><h6>Later</h6><p>{row.current ? display(row.current.value) : "No later value"}</p>{row.current && <p>State: {row.current.state.replaceAll("-", " ")}{row.current.reasons.length ? ` · ${row.current.reasons.join("; ")}` : ""}</p>}</section>
          </div>
          {row.reason && <p><strong>Reason:</strong> {row.reason}</p>}
          {row.relation && <p><strong>Retained relation:</strong> {row.relation.relation.replaceAll("-", " ")} · <code>{row.relation.fromIdentityKey}</code> → <code>{row.relation.toIdentityKey}</code></p>}
          <details><summary>Inspect provenance ({row.refs.length} reference{row.refs.length === 1 ? "" : "s"})</summary><ul>{row.refs.map(sourceRefLabel)}</ul></details>
        </article>
      </li>)}
    </ol>
    {pageCount > 1 && <nav aria-label={`${family} ${kind} detail pages`}>
      <button type="button" onClick={() => setPage((value) => Math.max(0, value - 1))} disabled={page === 0}>Previous 20</button>
      <span>Page {page + 1} of {pageCount}; all {rows.length} records are retained.</span>
      <button type="button" onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))} disabled={page + 1 === pageCount}>Next 20</button>
    </nav>}
  </details>;
}

function RowSection({ title, rows }: SectionRows) {
  const groups = useMemo(() => {
    const map = new Map<string, ChangeRowV1[]>();
    for (const row of rows) {
      const key = `${row.family}\u0000${row.kind}`;
      const group = map.get(key) ?? [];
      group.push(row);
      map.set(key, group);
    }
    return Array.from(map.entries()).map(([key, values]) => {
      const [family, kind] = key.split("\u0000");
      return { family: family!, kind: kind!, rows: values };
    });
  }, [rows]);
  return <section aria-label={title}>
    <h4>{title} ({rows.length})</h4>
    {groups.length ? groups.map((group) => <RowGroup key={`${group.family}:${group.kind}`} {...group} />) : <p>No {title.toLowerCase()} were reported.</p>}
  </section>;
}

function familyLabel(family: FamilyCoverageV1) {
  const state = (value: string) => value.replaceAll("-", " ");
  return <li key={family.family}><strong>{family.family}</strong>: earlier {state(family.left)}, later {state(family.right)}; {family.change.replaceAll("-", " ")} · semantic coverage {family.semanticCoverage}.</li>;
}

function UnresolvedRows({ rows }: { rows: EventEvidenceComparisonPacketV1["result"]["unresolved"] }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const visible = rows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
  return <section aria-label="Unresolved comparison items"><h4>Unresolved comparison items ({rows.length})</h4>
    <ol start={page * ROWS_PER_PAGE + 1}>{visible.map((item, index) => <li key={`${item.code}:${item.identityKey}:${page}:${index}`}>
      <p>{item.code.replaceAll("-", " ")} · {item.family ?? "family unavailable"} · {item.identityKey ?? "identity unavailable"}.</p>
      <details><summary>Inspect provenance ({item.refs.length})</summary><ul>{item.refs.map(sourceRefLabel)}</ul></details>
    </li>)}</ol>
    {pageCount > 1 && <nav aria-label="Unresolved comparison pages"><button type="button" onClick={() => setPage((value) => Math.max(0, value - 1))} disabled={page === 0}>Previous 20</button><span>Page {page + 1} of {pageCount}; all {rows.length} records are retained.</span><button type="button" onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))} disabled={page + 1 === pageCount}>Next 20</button></nav>}
  </section>;
}

const EventEvidenceComparison: React.FC<Props> = ({ dossierId, asOf, boundaryValid = true }) => {
  const [versions, setVersions] = useState<VersionListItemV1[]>([]);
  const [nextBeforeSequence, setNextBeforeSequence] = useState<number | null>(null);
  const [readState, setReadState] = useState<ReadState>("loading");
  const [readError, setReadError] = useState("");
  const [pageBusy, setPageBusy] = useState(false);
  const [reloadEpoch, setReloadEpoch] = useState(0);
  const [earlierId, setEarlierId] = useState("");
  const [laterId, setLaterId] = useState("");
  const [packet, setPacket] = useState<EventEvidenceComparisonPacketV1 | null>(null);
  const [packetBody, setPacketBody] = useState<string | null>(null);
  const [compareState, setCompareState] = useState<"idle" | "working" | "error">("idle");
  const [compareError, setCompareError] = useState("");
  const pageRequest = useRef<AbortController | null>(null);
  const compareRequest = useRef<AbortController | null>(null);
  const compareGeneration = useRef(0);
  const generation = useRef(0);
  const comparisonContext = useRef({ dossierId, asOf, earlierId, laterId });
  comparisonContext.current = { dossierId, asOf, earlierId, laterId };

  const invalidateComparison = () => {
    compareGeneration.current += 1;
    compareRequest.current?.abort();
    setPacket(null);
    setPacketBody(null);
    setCompareError("");
    setCompareState("idle");
  };

  useEffect(() => {
    const requestGeneration = ++generation.current;
    compareGeneration.current += 1;
    pageRequest.current?.abort();
    compareRequest.current?.abort();
    const controller = new AbortController();
    pageRequest.current = controller;
    setVersions([]);
    setNextBeforeSequence(null);
    setEarlierId("");
    setLaterId("");
    setPacket(null);
    setPacketBody(null);
    setReadError("");
    setCompareError("");
    setCompareState("idle");
    if (!boundaryValid) { setReadState("error"); setReadError("Enter a canonical UTC boundary before loading saved versions."); return () => controller.abort(); }
    setReadState("loading");
    const query = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
    void fetch(`/api/research-dossiers/events/${encodeURIComponent(dossierId)}/versions${query}`, {
      headers: getClientIdentityHeaders(), credentials: "include", cache: "no-store", signal: controller.signal,
    }).then(async (response) => {
      const body = await readJson(response);
      if (requestGeneration !== generation.current || controller.signal.aborted) return;
      if (!response.ok) throw new Error(errorMessage(body, `Saved versions could not be loaded (HTTP ${response.status}).`));
      if (!validVersionPage(body)) throw new Error("The saved-version response is incomplete or unsupported.");
      if (body.dossier.id !== dossierId || body.asOf !== asOf) throw new Error("The saved-version response does not match this dossier and UTC boundary.");
      setVersions([...body.versions]);
      setNextBeforeSequence(body.nextBeforeSequence);
      setReadState(body.versions.length ? "ready" : "empty");
    }).catch((cause: unknown) => {
      if (requestGeneration !== generation.current || controller.signal.aborted) return;
      setReadError(cause instanceof Error ? cause.message : "Saved versions could not be loaded.");
      setReadState("error");
    });
    return () => controller.abort();
  }, [dossierId, asOf, boundaryValid, reloadEpoch]);

  const selectable = versions.filter((version) => version.receiptState === "verified" && version.acknowledgedAt !== null);
  const selectedEarlier = selectable.find((version) => version.reportId === earlierId) ?? null;
  const selectedLater = selectable.find((version) => version.reportId === laterId) ?? null;
  const orderedPair = selectedEarlier && selectedLater
    ? selectedEarlier.reportId === selectedLater.reportId || selectedEarlier.sequence === selectedLater.sequence
      ? null
      : selectedEarlier.sequence < selectedLater.sequence ? [selectedEarlier, selectedLater] as const : [selectedLater, selectedEarlier] as const
    : null;

  const loadOlder = async () => {
    if (nextBeforeSequence === null || pageBusy) return;
    pageRequest.current?.abort();
    const controller = new AbortController();
    pageRequest.current = controller;
    const requestGeneration = generation.current;
    setPageBusy(true);
    setReadError("");
    const query = new URLSearchParams({ beforeSequence: String(nextBeforeSequence) });
    if (asOf) query.set("asOf", asOf);
    try {
      const response = await fetch(`/api/research-dossiers/events/${encodeURIComponent(dossierId)}/versions?${query.toString()}`, {
        headers: getClientIdentityHeaders(), credentials: "include", cache: "no-store", signal: controller.signal,
      });
      const body = await readJson(response);
      if (requestGeneration !== generation.current || controller.signal.aborted) return;
      if (!response.ok) throw new Error(errorMessage(body, `Older versions could not be loaded (HTTP ${response.status}).`));
      if (!validVersionPage(body) || body.dossier.id !== dossierId || body.asOf !== asOf) throw new Error("The older-version response does not match this dossier and UTC boundary.");
      setVersions((current) => {
        const seen = new Set(current.map((version) => version.reportId));
        return [...current, ...body.versions.filter((version) => !seen.has(version.reportId))];
      });
      setNextBeforeSequence(body.nextBeforeSequence);
    } catch (cause) {
      if (requestGeneration === generation.current && !controller.signal.aborted) setReadError(cause instanceof Error ? cause.message : "Older versions could not be loaded.");
    } finally {
      if (requestGeneration === generation.current) setPageBusy(false);
    }
  };

  const compare = async () => {
    if (!orderedPair || !boundaryValid) return;
    compareRequest.current?.abort();
    const controller = new AbortController();
    compareRequest.current = controller;
    const requestGeneration = ++compareGeneration.current;
    const request = {
      dossierId,
      fromReportId: orderedPair[0].reportId,
      fromSequence: orderedPair[0].sequence,
      toReportId: orderedPair[1].reportId,
      toSequence: orderedPair[1].sequence,
      asOf,
      selectedEarlierId: earlierId,
      selectedLaterId: laterId,
    };
    setPacket(null);
    setPacketBody(null);
    setCompareError("");
    setCompareState("working");
    const query = new URLSearchParams({ fromReportId: request.fromReportId, toReportId: request.toReportId });
    if (request.asOf) query.set("asOf", request.asOf);
    try {
      const response = await fetch(`/api/research-dossiers/events/${encodeURIComponent(dossierId)}/compare?${query.toString()}`, {
        headers: getClientIdentityHeaders(), credentials: "include", cache: "no-store", signal: controller.signal,
      });
      let bodyText = "";
      try { bodyText = await response.text(); } catch { /* handled as an incomplete response below */ }
      let body: unknown = null;
      try { body = JSON.parse(bodyText); } catch { /* handled as an incomplete response below */ }
      if (requestGeneration !== compareGeneration.current || controller.signal.aborted) return;
      if (!response.ok) throw new Error(errorMessage(body, `Comparison could not be loaded (HTTP ${response.status}).`));
      if (!validPacket(body)) throw new Error("The comparison response is incomplete or unsupported.");
      if (comparisonContext.current.dossierId !== request.dossierId || comparisonContext.current.asOf !== request.asOf
        || comparisonContext.current.earlierId !== request.selectedEarlierId || comparisonContext.current.laterId !== request.selectedLaterId) return;
      if (!packetMatchesRequest(body, request)) throw new Error("The comparison response does not match the selected versions, dossier, or UTC boundary.");
      setPacket(body);
      setPacketBody(bodyText);
      setCompareState("idle");
    } catch (cause) {
      if (requestGeneration === compareGeneration.current && !controller.signal.aborted) {
        setCompareError(cause instanceof Error ? cause.message : "Comparison could not be loaded.");
        setCompareState("error");
      }
    }
  };

  const downloadPacket = () => {
    if (!packet || packetBody === null) return;
    const blob = new Blob([packetBody], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `tsm-event-evidence-comparison-${packet.left.metadata.reportId}-${packet.right.metadata.reportId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return <section className="event-evidence-comparison" aria-labelledby="event-evidence-comparison-title">
    <h4 id="event-evidence-comparison-title">Compare saved event versions</h4>
    <p>Compare two acknowledged saved versions from this dossier. This read-only request does not create a report or change retained evidence.</p>
    {asOf && <p><strong>UTC boundary:</strong> {asOf}. Only verified acknowledgments at or before this time are listed.</p>}
    {!asOf && <p>Without a boundary, this view makes no historical known-since claim. Missing or invalid receipts are visible but cannot be selected.</p>}
    {readState === "loading" && <p role="status">Loading acknowledged saved versions…</p>}
    {readState === "error" && <div role="alert"><p>{readError}</p><button type="button" onClick={() => setReloadEpoch((value) => value + 1)}>Retry version list</button></div>}
    {readState === "empty" && <p role="status">No saved versions are available at this boundary.</p>}
    {(readState === "ready" || readState === "empty") && versions.length > 0 && <>
      <div className="event-evidence-comparison-selectors" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 18rem), 1fr))", gap: "1rem" }}>
        <div><label htmlFor={`event-compare-earlier-${dossierId}`}>First saved version</label><select id={`event-compare-earlier-${dossierId}`} value={earlierId} onChange={(event) => { invalidateComparison(); setEarlierId(event.target.value); }}>
          <option value="">Select a version</option>
          {versions.map((version) => <option key={`earlier-${version.reportId}`} value={version.reportId} disabled={version.receiptState !== "verified" || !version.acknowledgedAt}>{`Version ${version.sequence} · ${version.reportId.slice(0, 8)} · ${version.receiptState === "verified" ? "acknowledged" : version.receiptState}`}</option>)}
        </select></div>
        <div><label htmlFor={`event-compare-later-${dossierId}`}>Second saved version</label><select id={`event-compare-later-${dossierId}`} value={laterId} onChange={(event) => { invalidateComparison(); setLaterId(event.target.value); }}>
          <option value="">Select a version</option>
          {versions.map((version) => <option key={`later-${version.reportId}`} value={version.reportId} disabled={version.receiptState !== "verified" || !version.acknowledgedAt}>{`Version ${version.sequence} · ${version.reportId.slice(0, 8)} · ${version.receiptState === "verified" ? "acknowledged" : version.receiptState}`}</option>)}
        </select></div>
      </div>
      <ul aria-label="Saved version receipt states">{versions.map((version) => <li key={`receipt-${version.reportId}`}>Version {version.sequence} · report <code>{version.reportId}</code> · {receiptLabel(version)}</li>)}</ul>
      <p>Versions in this page: {versions.length}. Selection is retained while loading older pages so you can compare versions across pages.</p>
      {nextBeforeSequence !== null && <button type="button" onClick={() => void loadOlder()} disabled={pageBusy}>{pageBusy ? "Loading older versions…" : "Load older versions"}</button>}
      {readError && <p role="alert">{readError}</p>}
      {orderedPair && <p><strong>Sequence order:</strong> earlier version {orderedPair[0].sequence} (<code>{orderedPair[0].reportId}</code>) → later version {orderedPair[1].sequence} (<code>{orderedPair[1].reportId}</code>). User selection order does not change chronology.</p>}
      <button type="button" onClick={() => void compare()} disabled={!orderedPair || compareState === "working" || !boundaryValid}>{compareState === "working" ? "Comparing saved versions…" : "Compare these saved versions"}</button>
    </>}
    {compareState === "working" && <p role="status">Verifying and comparing the two saved versions…</p>}
    {compareState === "error" && <p role="alert">{compareError}</p>}
    {packet && <div className="event-evidence-comparison-result" aria-label="Saved event version comparison">
      <header><h4>Comparison result</h4><button type="button" onClick={downloadPacket}>Download exact comparison packet</button></header>
      <p>Packet digest <code>{packet.digest}</code> · comparison {packet.result.status} · semantic coverage {packet.result.semanticCoverage} · presentation {packet.result.presentation.status.replaceAll("-", " ")}.</p>
      <p><strong>Earlier:</strong> version {packet.left.metadata.sequence} · report <code>{packet.left.metadata.reportId}</code> · saved {dateLabel(packet.left.metadata.createdAt)} · capture {dateLabel(packet.left.metadata.captureAsOf)} · acknowledged {dateLabel(packet.left.metadata.acknowledgedAt)}.</p>
      <p><strong>Later:</strong> version {packet.right.metadata.sequence} · report <code>{packet.right.metadata.reportId}</code> · saved {dateLabel(packet.right.metadata.createdAt)} · capture {dateLabel(packet.right.metadata.captureAsOf)} · acknowledged {dateLabel(packet.right.metadata.acknowledgedAt)}.</p>
      <p><strong>Clock note:</strong> source clocks, request/response clocks, retention clocks, capture clocks, saved-version clocks, and receipt acknowledgment clocks describe separate events. A clock-only change is observation metadata, not a changed source fact.</p>
      <p><strong>Trust boundary:</strong> online owner and dossier membership was verified for this request. The offline digest is an unkeyed checksum; it does not authenticate that claim.</p>
      <p><strong>Confidence:</strong> comparison not provided. Analytical confidence was not retained in event-review versions 4–10.</p>
      <section aria-label="Saved family coverage"><h4>Evidence family coverage</h4><ul>{packet.result.familyCoverage.map(familyLabel)}</ul></section>
      <RowSection title="Meaningful changes and retained facts" rows={packet.result.changes} />
      <RowSection title="Uncertainty" rows={packet.result.uncertainty} />
      <RowSection title="Evidence gaps" rows={packet.result.gaps} />
      <section aria-label="Saved conclusions"><h4>Conclusions ({packet.result.conclusions.length})</h4>
        {packet.result.conclusions.length ? <ul>{packet.result.conclusions.map((conclusion, index) => <li key={`${conclusion.side}:${conclusion.family}:${index}`}><p><strong>{conclusion.side === "left" ? "Earlier" : "Later"} · {conclusion.family}:</strong> status {conclusion.status} · identity {conclusion.identityState ?? "not retained"} · evolution {conclusion.evolution ?? "not retained"} · history {conclusion.historyState ?? "not retained"} · stop {conclusion.stopReason ?? "not retained"}.</p><p>Supported comparison entries: {conclusion.supportedByEntryDigests.length ? conclusion.supportedByEntryDigests.map((digest) => <code key={digest}>{digest} </code>) : "none; relationship is not established"}</p><details><summary>Conclusion provenance ({conclusion.sourceRefs.length})</summary><ul>{conclusion.sourceRefs.map(sourceRefLabel)}</ul></details></li>)}</ul> : <p>No conclusion records were returned.</p>}
      </section>
      {packet.result.unresolved.length > 0 && <UnresolvedRows rows={packet.result.unresolved} />}
      <p>More retained evidence or a different result label does not establish greater analytical confidence or explain causation. Inspect each grouped record and its provenance for full detail.</p>
    </div>}
  </section>;
};

export default EventEvidenceComparison;
